/**
 * Bar Color Processor
 *
 * Applies bar coloring to chart data based on selected provider.
 * Attaches __barColors property to each data point.
 */

import {
  getBarColorProvider,
  DEFAULT_BAR_COLORS,
} from "../../../Registry/BarColorProviders/barColorRegistry";
import { isFeatureEnabled } from "../../../data/Services/settings";

const HEX_COLOR = /^#[0-9a-f]{3}([0-9a-f]{3})?$/i;

/**
 * Paleta efetiva de UM reprocessamento (#112). Resolvida UMA vez por chamada de
 * applyBarColors — nunca por barra: `getColors` roda para cada ponto e alocar/
 * mesclar objeto lá dentro sujaria o caminho de dados do chart (I5).
 *
 * Cai nas cores padrão quando: o flag está desligado (kill-switch — a paleta
 * salva no desktop é ignorada, sem rebuild), o método não tem semântica
 * alta/baixa, ou o valor gravado não é uma cor hex válida.
 */
export function resolveBarColorPalette(provider, barColorConfig) {
  if (!provider?.supportsCustomColors) return DEFAULT_BAR_COLORS;
  // USE time: o flag é lido aqui, no reprocessamento, nunca no escopo do módulo.
  if (!isFeatureEnabled("barColorCustom")) return DEFAULT_BAR_COLORS;

  const custom = barColorConfig?.colors;
  if (!custom) return DEFAULT_BAR_COLORS;

  let changed = false;
  const palette = { ...DEFAULT_BAR_COLORS };
  for (const key of ["upStroke", "downStroke", "upFill", "downFill"]) {
    const value = custom[key];
    if (typeof value === "string" && HEX_COLOR.test(value)) {
      palette[key] = value;
      changed = true;
    }
  }
  // Sem nenhuma cor válida, devolve a MESMA referência do default — mantém o
  // caminho comum livre de alocação.
  return changed ? palette : DEFAULT_BAR_COLORS;
}

/**
 * Apply bar colors to data array
 *
 * @param {Array} data - Chart data array
 * @param {string} providerKey - Bar color provider key ('traditional', 'previousClose', 'volumeComparison', 'indicator:key')
 * @param {Object} barColorConfig - Additional config for provider (optional)
 * @param {Array} indicators - Active indicators (for indicator-based providers)
 * @returns {Array} Data with __barColors property added to each point
 */
export function applyBarColors(
  data,
  providerKey = "traditional",
  barColorConfig = {},
  indicators = [],
) {
  if (!data || data.length === 0) {
    return data;
  }

  const provider = getBarColorProvider(providerKey);

  if (!provider) {
    console.warn(
      `[BarColorProcessor] Provider not found: ${providerKey}, using traditional`,
    );
    return applyBarColors(data, "traditional", barColorConfig, indicators);
  }

  try {
    // Build indicator config map for quick lookup
    const indicatorConfigMap = {};
    if (provider.requiresIndicators && provider.requiresIndicators.length > 0) {
      provider.requiresIndicators.forEach((requiredKey) => {
        const indicator = indicators.find((ind) => ind.key === requiredKey);
        if (indicator) {
          indicatorConfigMap[requiredKey] = indicator;
        }
      });
      // (Havia 5 console.log de debug aqui: rodavam a CADA reprocessamento com
      // método baseado em indicador — inundação de console no caminho de dados
      // do chart, contra a regra de performance do projeto. Removidos.)
    }

    // Paleta resolvida UMA vez para todas as barras (#112) — ver
    // resolveBarColorPalette.
    const palette = resolveBarColorPalette(provider, barColorConfig);

    // Apply colors using provider function
    const coloredData = data.map((d, index) => {
      try {
        const colors = provider.getColors(
          d,
          index,
          data,
          indicatorConfigMap,
          palette,
        );

        return {
          ...d,
          __barColors: colors,
        };
      } catch (err) {
        console.error(
          `[BarColorProcessor] Error processing bar ${index}:`,
          err,
        );
        // Fallback to traditional colors on error
        const isUp = d.close > d.open;
        return {
          ...d,
          __barColors: {
            fill: isUp ? palette.upFill : palette.downFill,
            stroke: isUp ? palette.upStroke : palette.downStroke,
            wickStroke: palette.wickStroke,
          },
        };
      }
    });

    return coloredData;
  } catch (err) {
    console.error(`[BarColorProcessor] Fatal error applying colors:`, err);
    return data; // Return original data on fatal error
  }
}

/**
 * Check if data has bar colors applied
 */
export function hasBarColors(data) {
  return data && data.length > 0 && data[0].__barColors !== undefined;
}

/**
 * Remove bar colors from data
 */
export function removeBarColors(data) {
  if (!data || data.length === 0) {
    return data;
  }

  return data.map((d) => {
    const { __barColors, ...rest } = d;
    return rest;
  });
}
